import {
  minimapDockWidth,
  minimapDockWidthLarge,
  minimapDockWidthMedium,
} from '@blocksuite/affine-shared/styles';
import {
  focusElementsOnScreen,
  stopPropagation,
} from '@blocksuite/affine-shared/utils';
import { WithDisposable } from '@blocksuite/global/lit';
import type { BlockStdScope } from '@blocksuite/std';
import {
  GfxControllerIdentifier,
  type GfxNavigationHost,
  ZOOM_STEP,
} from '@blocksuite/std/gfx';
import { effect } from '@preact/signals-core';
import { css, html, LitElement, nothing, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';

import { defineZoomControls, type ZoomControlsTarget } from './zoom-controls';

/** The canvas map with the zoom bar under it. */
export class EdgelessZoomToolbar extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      display: flex;
    }

    .edgeless-zoom-toolbar-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: ${unsafeCSS(minimapDockWidth)};
    }

    @container viewport (width > 1024px) and (width <= 1440px) {
      .edgeless-zoom-toolbar-container {
        width: ${unsafeCSS(minimapDockWidthMedium)};
      }
    }

    @container viewport (width > 1440px) {
      .edgeless-zoom-toolbar-container {
        width: ${unsafeCSS(minimapDockWidthLarge)};
      }
    }

    /* Minimal wrapper; the minimap component owns its internal styles. */
    .minimap-wrapper {
      width: 100%;
      margin-bottom: 8px;
    }
  `;

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  get viewport() {
    return this.navigationHost?.gfx.viewport ?? this.gfx.viewport;
  }

  @query('edgeless-minimap-canvas')
  private accessor _minimap!: Element | null;

  @state()
  private accessor _maximapOpen = false;

  /** The canvas viewport, as the zoom bar steers it. */
  private readonly _target = this._createTarget();

  private _createTarget(): ZoomControlsTarget {
    const toolbar = this;
    return {
      get zoom() {
        return toolbar.viewport.zoom;
      },
      get locked() {
        return toolbar.viewport.locked;
      },
      observe(onChange) {
        const subscription =
          toolbar.viewport.viewportUpdated.subscribe(onChange);
        return () => subscription.unsubscribe();
      },
      fit: () => toolbar._fitSelectionOrScreen(),
      zoomOut: () => toolbar.viewport.zoomByStep(-ZOOM_STEP),
      zoomIn: () => toolbar.viewport.zoomByStep(ZOOM_STEP),
      actualSize: () => toolbar.viewport.smoothZoom(1),
      openOverview: () =>
        toolbar._minimap?.dispatchEvent(new Event('open-maximap')),
    };
  }

  private _fitSelectionOrScreen() {
    if (this.navigationHost) return this.navigationHost.fitToScreen();
    if (focusElementsOnScreen(this.gfx, this.gfx.selection.selectedElements)) {
      return;
    }
    this.gfx.fitToScreen({ smooth: true });
  }

  /** Open the canvas overview, or close it if it is open. */
  toggleOverview() {
    this._minimap?.dispatchEvent(new Event('toggle-maximap'));
  }

  override connectedCallback() {
    super.connectedCallback();
    defineZoomControls();
    if (!this.navigationHost)
      this.disposables.add(
        effect(() => {
          this.gfx.tool.currentToolName$.value;
          this.requestUpdate();
        })
      );
  }

  override render() {
    // Hide entire zoom toolbar + minimap during presentation mode. Read-only
    // users still need viewport navigation, which does not mutate document data.
    if (
      !this.navigationHost &&
      this.gfx.tool.currentToolName$.value === 'frameNavigator'
    ) {
      return nothing;
    }

    return html`
      <div
        class="edgeless-zoom-toolbar-container"
        @dblclick=${stopPropagation}
        @mousedown=${stopPropagation}
        @mouseup=${stopPropagation}
        @pointerdown=${stopPropagation}
      >
        <div class="minimap-wrapper">
          <edgeless-minimap-canvas
            .std=${this.std}
            .host=${this.navigationHost}
            @maximap-open-changed=${(event: CustomEvent<boolean>) => {
              this._maximapOpen = event.detail;
            }}
          ></edgeless-minimap-canvas>
        </div>
        <streamlain-zoom-controls
          .target=${this._target}
          .overviewOpen=${this._maximapOpen}
        ></streamlain-zoom-controls>
      </div>
    `;
  }

  @property({ attribute: false })
  accessor std!: BlockStdScope;

  @property({ attribute: false })
  accessor navigationHost: GfxNavigationHost | undefined;
}
